import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatRelative } from "../lib/api";
import AppShell from "../components/AppShell";
import { useAuth } from "../lib/auth";
import { Television, ArrowsClockwise, Broadcast, Play, ChartBar } from "@phosphor-icons/react";

const CONTENT_TYPE_LABEL = {
  livestream: "LIVE",
  news: "NEWS",
  review: "REVIEW",
  documentary: "DOC",
  broadcast: "BROADCAST",
  explainer: "EXPLAINER",
};

export default function LiveTV() {
  const { user } = useAuth();
  const [videos, setVideos] = useState(null);
  const [corridors, setCorridors] = useState([]);
  const [active, setActive] = useState("all");
  const [showLiveOnly, setShowLiveOnly] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [playing, setPlaying] = useState(null);

  const load = async () => {
    const params = {};
    if (active !== "all") params.corridor = active;
    if (showLiveOnly) params.live_only = true;
    const { data } = await api.get("/live-tv", { params });
    setVideos(data);
  };

  useEffect(() => {
    api.get("/live-tv/corridors").then((r) => setCorridors(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [active, showLiveOnly]);

  const corridorMap = useMemo(() => Object.fromEntries((corridors || []).map((c) => [c.key, c])), [corridors]);

  const refresh = async () => {
    if (refreshing || !user || user.role !== "admin") return;
    setRefreshing(true);
    try {
      await api.post("/live-tv/refresh");
      await load();
    } catch (_) {}
    finally { setRefreshing(false); }
  };

  const open = async (v) => {
    setPlaying(v);
    try { await api.post(`/live-tv/${v.video_id}/watch`); } catch (_) {}
  };

  // Split into LIVE NOW / FEATURED / THIS WEEK
  const liveNow = (videos || []).filter((v) => v.is_live);
  const recent = (videos || []).filter((v) => !v.is_live);
  // top relevance non-live → "Featured"
  const featured = [...recent].sort((a, b) => b.relevance_score - a.relevance_score).slice(0, 6);
  const featuredIds = new Set(featured.map((v) => v.id));
  const thisWeek = recent.filter((v) => !featuredIds.has(v.id));

  return (
    <AppShell>
      <div data-testid="live-tv-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="label-mono mb-2 flex items-center gap-2" style={{ color: "#E60012" }}>
              <Television size={12} weight="fill" /> LIVE MOBILITY TV · 24/7 CURATED FEED
            </div>
            <h1 className="font-display text-white mb-3" style={{ fontSize: "clamp(36px, 5vw, 56px)", fontWeight: 900, letterSpacing: "-0.035em", lineHeight: 0.98 }}>
              Future mobility,<br /><span style={{ color: "#E60012" }}>in motion.</span>
            </h1>
            <p className="text-zinc-400 max-w-2xl">
              Curated by <span className="text-white">MEDIA-CURATOR-01</span> — live broadcasts, breakdowns,
              and field documentaries from the channels actually building the next generation of mobility.
            </p>
          </div>
          {user && user.role === "admin" && (
            <Link to="#" onClick={refresh} className="btn btn-ghost btn-sm" data-testid="livetv-refresh-btn">
              {refreshing ? <span className="spinner mr-2" /> : <ArrowsClockwise size={12} weight="bold" />}
              {refreshing ? "Curating..." : "Refresh feed"}
            </Link>
          )}
        </div>

        {/* Corridor chips */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="livetv-corridor-chips">
          {corridors.map((c) => (
            <button
              key={c.key}
              onClick={() => setActive(c.key)}
              className={`label-mono px-3 py-1.5 border flex items-center gap-1.5 ${active === c.key ? "border-white text-white bg-[#1A1A1D]" : "border-[#27272A] text-zinc-400 hover:text-white"}`}
              style={active === c.key ? { borderColor: c.color, color: c.color } : {}}
              data-testid={`livetv-chip-${c.key}`}
            >
              {c.key !== "all" && <span className="hub-dot" style={{ color: c.color }} />}
              {c.name.toUpperCase()}
            </button>
          ))}
          <button
            onClick={() => setShowLiveOnly((v) => !v)}
            className={`label-mono px-3 py-1.5 border flex items-center gap-1.5 ${showLiveOnly ? "text-white" : "text-zinc-400 hover:text-white"}`}
            style={showLiveOnly ? { borderColor: "#E60012", color: "#E60012" } : { borderColor: "#27272A" }}
            data-testid="livetv-live-toggle"
          >
            <Broadcast size={10} weight={showLiveOnly ? "fill" : "regular"} /> LIVE ONLY
          </button>
        </div>

        {videos === null ? (
          <div className="py-16 text-center label-mono"><span className="spinner mr-2 align-middle" />LOADING FEED...</div>
        ) : (
          <>
            {/* LIVE NOW */}
            {liveNow.length > 0 && (
              <section className="mb-12" data-testid="livetv-section-live">
                <div className="flex items-center gap-3 mb-4">
                  <span className="hub-dot pulse" style={{ color: "#E60012" }} />
                  <h2 className="font-display text-white text-xl" style={{ fontWeight: 800, letterSpacing: "-0.02em" }}>LIVE NOW</h2>
                  <span className="label-mono">{liveNow.length} BROADCAST{liveNow.length === 1 ? "" : "S"}</span>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {liveNow.map((v) => <VideoCard key={v.id} v={v} corridorMap={corridorMap} onOpen={open} live />)}
                </div>
              </section>
            )}

            {/* FEATURED */}
            {featured.length > 0 && (
              <section className="mb-12" data-testid="livetv-section-featured">
                <div className="flex items-center gap-3 mb-4">
                  <Play size={14} weight="fill" style={{ color: "#E60012" }} />
                  <h2 className="font-display text-white text-xl" style={{ fontWeight: 800, letterSpacing: "-0.02em" }}>FEATURED CHANNELS</h2>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {featured.map((v) => <VideoCard key={v.id} v={v} corridorMap={corridorMap} onOpen={open} />)}
                </div>
              </section>
            )}

            {/* THIS WEEK */}
            {thisWeek.length > 0 && (
              <section data-testid="livetv-section-week">
                <div className="flex items-center gap-3 mb-4">
                  <ChartBar size={14} weight="bold" style={{ color: "#E60012" }} />
                  <h2 className="font-display text-white text-xl" style={{ fontWeight: 800, letterSpacing: "-0.02em" }}>THIS WEEK</h2>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {thisWeek.map((v) => <VideoCard key={v.id} v={v} corridorMap={corridorMap} onOpen={open} />)}
                </div>
              </section>
            )}

            {liveNow.length === 0 && featured.length === 0 && thisWeek.length === 0 && (
              <div className="border border-dashed border-[#27272A] p-12 text-center" data-testid="livetv-empty">
                <Television size={32} weight="light" className="mx-auto mb-3 text-zinc-600" />
                <div className="label-mono mb-2">FEED CURATING</div>
                <p className="text-zinc-400 text-sm">MEDIA-CURATOR-01 is sourcing the latest mobility broadcasts. Check back in a minute.</p>
              </div>
            )}
          </>
        )}

        {/* Inline player modal */}
        {playing && (
          <div
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setPlaying(null)}
            data-testid="livetv-player-modal"
          >
            <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
              <div className="aspect-video bg-black border border-[#27272A] relative">
                <iframe
                  title={playing.title}
                  src={`${playing.embed_url}&autoplay=1`}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                  data-testid="livetv-player-iframe"
                />
              </div>
              <div className="border border-t-0 border-[#27272A] bg-[#0A0A0C] p-4 flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="label-mono mb-1">{playing.channel_name.toUpperCase()}</div>
                  <div className="text-sm text-white line-clamp-1">{playing.title}</div>
                </div>
                <a
                  href={playing.watch_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary btn-sm"
                  data-testid="livetv-player-youtube"
                >
                  <Play size={11} weight="fill" /> Watch on YouTube
                </a>
                <button onClick={() => setPlaying(null)} className="btn btn-ghost btn-sm" data-testid="livetv-player-close">
                  Close
                </button>
              </div>
              <p className="text-xs text-zinc-500 mt-2 text-center">
                Some channels disable in-page embedding — click <span className="text-white">Watch on YouTube</span> if the player shows "Video unavailable".
              </p>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function VideoCard({ v, corridorMap, onOpen, live = false }) {
  const c = corridorMap[v.corridor] || { name: v.corridor, color: "#E60012" };
  const label = live ? "LIVE" : (CONTENT_TYPE_LABEL[v.content_type] || "VIDEO");
  return (
    <button
      onClick={() => onOpen(v)}
      className="card card-hover overflow-hidden flex flex-col text-left"
      data-testid={`livetv-card-${v.video_id}`}
      style={{ borderTop: `2px solid ${live ? "#E60012" : c.color}` }}
    >
      <div className="aspect-video bg-[#0A0A0C] relative overflow-hidden">
        {v.thumbnail_url ? (
          <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" loading="lazy"
               onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <div className="w-full h-full blueprint-grid-dense flex items-center justify-center">
            <Television size={28} weight="light" className="text-zinc-700" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C]/80 via-transparent" />
        <div className="absolute top-2 left-2 label-mono px-2 py-1 bg-[#0A0A0C]/90 border flex items-center gap-1" style={{ color: live ? "#E60012" : c.color, borderColor: live ? "#E60012" : c.color }}>
          {live ? <span className="hub-dot pulse mr-0.5" style={{ color: "#E60012" }} /> : <span className="hub-dot mr-0.5" style={{ color: c.color }} />}
          {label}
        </div>
        <div className="absolute bottom-2 right-2 w-12 h-12 rounded-full bg-[#E60012]/80 flex items-center justify-center group-hover:scale-110 transition-transform">
          <Play size={18} weight="fill" className="text-white ml-0.5" />
        </div>
      </div>
      <div className="p-4 flex flex-col flex-1">
        <div className="label-mono mb-2">
          {v.channel_name.toUpperCase()} · {formatRelative(v.published_at)}
        </div>
        <div className="text-white text-sm leading-snug line-clamp-3 flex-1 mb-2">{v.title}</div>
        {v.tags && v.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-auto">
            {v.tags.slice(0, 3).map((t) => (
              <span key={t} className="label-mono text-zinc-500">#{t.toUpperCase()}</span>
            ))}
          </div>
        )}
      </div>
    </button>
  );
}
